import {
  Col,
  Input,
  message,
  PageHeader,
  Row,
  Spin,
  Select,
  Space,
  Tabs,
  Tag,
} from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  SelectItem,
  Form,
  FormItem,
  TableItem,
  ButtonItem,
  ErrorItem,
} from "../../../../Components/Items";
import { FileExcelOutlined } from "@ant-design/icons";
import * as XLSXStyle from "xlsx-js-style";
import { ResponsiveBar } from "@nivo/bar";
import moment from "moment";
import "moment/locale/es";

moment.locale("es");

const { Option } = Select;
const { TabPane } = Tabs;

// Buckets usados solo cuando la empresa no tiene configurado su propio
// esquema de rangosLotes/usarColoresLotes (pantalla Lotes / LotesProducto).
const BUCKETS_SIN_CONFIGURAR = [
  { key: "vencido", label: "Vencido", color: "#CF1322" },
  { key: "vigente", label: "Vigente", color: "#52C41A" },
];
const BUCKET_SIN_CADUCIDAD = { key: "sinCaducidad", label: "Sin Caducidad", color: "#8C8C8C" };
const BUCKET_FUERA_DE_RANGO = { key: "fueraDeRango", label: "Fuera De Rango", color: "#52C41A" };

// Extrae los componentes r,g,b de un color en hex ("#RRGGBB") o rgb(a)(...).
const componentesColor = (color) => {
  if (!color) return null;
  if (color.startsWith("rgb")) {
    const [r, g, b] = color.match(/[\d.]+/g).map(Number);
    return { r, g, b };
  }
  const limpio = color.replace("#", "");
  if (limpio.length !== 6) return null;
  return {
    r: parseInt(limpio.substring(0, 2), 16),
    g: parseInt(limpio.substring(2, 4), 16),
    b: parseInt(limpio.substring(4, 6), 16),
  };
};

// Version de baja opacidad del color configurado, para usarla como fondo
// sutil de fila en pantalla (CSS soporta alfa) sin importar que tan intenso
// sea el color original elegido por el administrador.
const suavizarColorCss = (color) => {
  const c = componentesColor(color);
  return c ? `rgba(${c.r}, ${c.g}, ${c.b}, 0.18)` : undefined;
};

// Mezcla del color hacia blanco al 18%, en hex plano: las celdas de Excel
// no soportan alfa, asi que el tono pastel debe resolverse aqui mismo.
const suavizarColorHex = (color) => {
  const c = componentesColor(color);
  if (!c) return undefined;
  const alpha = 0.18;
  const mezclar = (canal) => Math.round(canal * alpha + 255 * (1 - alpha));
  return [mezclar(c.r), mezclar(c.g), mezclar(c.b)]
    .map((canal) => canal.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
};

class ReporteLotes extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    objetoProyectos: null,
    todasProyectos: null,
    dataReporte: null,
    todosLotes: [],
    dataGrafico: [],
    filtroRango: "todos",
    busqueda: "",
    usarColoresLotes: false,
    rangosLotes: [],
    buckets: [...BUCKETS_SIN_CONFIGURAR, BUCKET_SIN_CADUCIDAD],
  };

  atras() {
    this.props.history.goBack();
  }

  async componentDidMount() {
    this.setState({ loading: true });
    await Promise.all([
      axios
        .post(
          "proyectos/selectProyectos",
          { empresaId: this.props.empresaId },
          { headers: this.props.headersToken }
        )
        .then((res) => {
          if (res.data.status !== 404) {
            this.setState({
              todasProyectos: res.data,
              objetoProyectos: res.data.map((value) => (
                <Option value={value._id} key={value._id}>
                  {value.nombre}
                </Option>
              )),
            });
          }
        }),
      axios
        .post(
          "lotes/selectConfiguracionLotes",
          { empresaId: this.props.empresaId },
          { headers: this.props.headersToken }
        )
        .then((res) => {
          if (res.data.success === 2) {
            const usarColoresLotes = !!res.data.usarColoresLotes;
            const rangosLotes = [...(res.data.rangosLotes || [])].sort((a, b) => a.tope - b.tope);
            this.setState({
              usarColoresLotes,
              rangosLotes,
              buckets: this.construirBuckets(usarColoresLotes, rangosLotes),
            });
          }
        }),
    ]);
    this.consultarReporteLotes({});
  }

  // Mismo esquema que usa la pantalla de Lotes: si la empresa configuro sus
  // propios rangosLotes (nombre/tope/color), esos son los buckets a usar; si
  // no, se cae a un esquema simple Vencido/Vigente.
  construirBuckets(usarColoresLotes, rangosLotes) {
    if (usarColoresLotes && rangosLotes.length > 0) {
      return [
        ...rangosLotes.map((rango, i) => ({
          key: `rango-${i}`,
          label: rango.nombre || `Rango ${i + 1}`,
          color: rango.color || "#8C8C8C",
        })),
        BUCKET_FUERA_DE_RANGO,
        BUCKET_SIN_CADUCIDAD,
      ];
    }
    return [...BUCKETS_SIN_CONFIGURAR, BUCKET_SIN_CADUCIDAD];
  }

  // Misma logica que Lotes.js::getIndiceRangoCaducidad: ordena los rangos
  // configurados ascendente por "tope" (dias restantes) y toma el primero
  // cuyo tope alcance los dias restantes del lote.
  obtenerRango(caducidad, diasRestantes) {
    if (!caducidad) return BUCKET_SIN_CADUCIDAD.key;
    const { usarColoresLotes, rangosLotes } = this.state;
    if (usarColoresLotes && rangosLotes.length > 0) {
      const idx = rangosLotes.findIndex((rango) => diasRestantes <= rango.tope);
      return idx !== -1 ? `rango-${idx}` : BUCKET_FUERA_DE_RANGO.key;
    }
    return diasRestantes < 0 ? "vencido" : "vigente";
  }

  crearGrafico(lotes) {
    const dataGrafico = this.state.buckets.map((bucket) => ({
      rango: bucket.label,
      Lotes: lotes.filter((lote) => lote.rango === bucket.key).length,
      color: bucket.color,
    }));
    this.setState({ dataGrafico });
  }

  consultarReporteLotes(values) {
    this.setState({ loading: true });
    axios
      .post(
        "reportesDeInventario/reporteLotes",
        { proyectoIds: values?.proyectos || [], empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          const hoy = moment().startOf("day");
          const lotes = (res.data.lotes || []).map((lote) => {
            const diasParaVencer = lote.caducidad
              ? moment(lote.caducidad).startOf("day").diff(hoy, "days")
              : null;
            return { ...lote, diasParaVencer, rango: this.obtenerRango(lote.caducidad, diasParaVencer) };
          });
          this.crearGrafico(lotes);
          this.setState({
            dataReporte: res.data,
            todosLotes: lotes,
            loading: false,
          });
        } else {
          message.error("Ha sucedido un error y no se cargo bien el reporte");
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error("Error de conexión");
        this.setState({ loading: false });
      });
  }

  obtenerLotesFiltrados() {
    const { todosLotes, filtroRango, busqueda } = this.state;
    const texto = busqueda.trim().toLowerCase();
    return todosLotes.filter((lote) => {
      if (filtroRango !== "todos" && lote.rango !== filtroRango) return false;
      if (texto) {
        const campo = `${lote.numero || ""} ${lote.insumoClave || ""} ${lote.insumoDescripcion || ""} ${lote.proyectoNombre || ""}`.toLowerCase();
        if (!campo.includes(texto)) return false;
      }
      return true;
    });
  }

  descargarExcel() {
    const lotes = this.obtenerLotesFiltrados();
    const { buckets } = this.state;
    const HEADER_COLOR = "D9D9D9";
    const border = {
      top: { style: "thin", color: { auto: 1 } },
      bottom: { style: "thin", color: { auto: 1 } },
      left: { style: "thin", color: { auto: 1 } },
      right: { style: "thin", color: { auto: 1 } },
    };
    const headerStyle = {
      font: { bold: true, sz: 11 },
      fill: { fgColor: { rgb: HEADER_COLOR } },
      alignment: { horizontal: "center", vertical: "center" },
      border,
    };
    const celdaTexto = (valor, fill) => ({
      v: valor || "",
      s: { border, ...(fill ? { fill: { fgColor: { rgb: fill } } } : {}) },
    });
    // xlsx-js-style no infiere el tipo de celda para objetos {v,s} ya armados;
    // sin t:"n" explicito el numero se escribe como texto y pierde el numFmt.
    const celdaNumero = (valor, fill) => ({
      v: valor || 0,
      t: "n",
      s: {
        border,
        alignment: { horizontal: "right" },
        numFmt: "#,##0.00",
        ...(fill ? { fill: { fgColor: { rgb: fill } } } : {}),
      },
    });
    const celdaFecha = (valor, fill) => ({
      v: valor ? moment(valor).format("DD/MM/YYYY") : "",
      s: { border, alignment: { horizontal: "center" }, ...(fill ? { fill: { fgColor: { rgb: fill } } } : {}) },
    });
    // No todos los lotes tienen caducidad capturada: deja la celda en blanco
    // en vez de forzar un 0 que se leería como "vence hoy".
    const celdaDias = (valor, fill) => (valor === null || valor === undefined ? celdaTexto("", fill) : celdaNumero(valor, fill));
    // Solo se resaltan las filas de rangos "de alerta" configurados por la
    // empresa (o el fallback "vencido"); sin caducidad/fuera de rango/vigente
    // se dejan sin color, igual que en la pantalla de Lotes.
    const colorDeFondo = (rangoKey) => {
      if (rangoKey === BUCKET_SIN_CADUCIDAD.key || rangoKey === BUCKET_FUERA_DE_RANGO.key || rangoKey === "vigente") {
        return undefined;
      }
      const bucket = buckets.find((b) => b.key === rangoKey);
      return bucket ? suavizarColorHex(bucket.color) : undefined;
    };

    const headerRow = [
      "Lote",
      "Clave Insumo",
      "Descripcion Insumo",
      "Proyecto",
      "Cantidad Disponible",
      "Fecha De Caducidad",
      "Dias Para Vencer",
    ].map((titulo) => ({ v: titulo, s: headerStyle }));

    const rows = [headerRow];
    lotes.forEach((lote) => {
      const fill = colorDeFondo(lote.rango);
      rows.push([
        celdaTexto(lote.numero, fill),
        celdaTexto(lote.insumoClave, fill),
        celdaTexto(lote.insumoDescripcion, fill),
        celdaTexto(lote.proyectoNombre, fill),
        celdaNumero(lote.disponible, fill),
        celdaFecha(lote.caducidad, fill),
        celdaDias(lote.diasParaVencer, fill),
      ]);
    });

    const worksheet = XLSXStyle.utils.aoa_to_sheet(rows);
    worksheet["!cols"] = [
      { wch: 16 },
      { wch: 16 },
      { wch: 34 },
      { wch: 22 },
      { wch: 18 },
      { wch: 18 },
      { wch: 16 },
    ];
    const workbook = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(workbook, worksheet, "Reporte Lotes");
    XLSXStyle.writeFile(workbook, `Reporte_Lotes_${moment().locale("es").format("DD-MM-YYYY")}.xlsx`);
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  render() {
    const lotesFiltrados = this.obtenerLotesFiltrados();
    const { buckets } = this.state;

    const columns = [
      {
        title: "Lote",
        dataIndex: "numero",
        key: "numero",
        sorter: (a, b) => (a.numero || "").localeCompare(b.numero || ""),
        ellipsis: true,
      },
      {
        title: "Clave",
        dataIndex: "insumoClave",
        key: "insumoClave",
        sorter: (a, b) => (a.insumoClave || "").localeCompare(b.insumoClave || ""),
        ellipsis: true,
      },
      {
        title: "Insumo",
        dataIndex: "insumoDescripcion",
        key: "insumoDescripcion",
        sorter: (a, b) => (a.insumoDescripcion || "").localeCompare(b.insumoDescripcion || ""),
        ellipsis: true,
      },
      {
        title: "Proyecto",
        dataIndex: "proyectoNombre",
        key: "proyectoNombre",
        sorter: (a, b) => (a.proyectoNombre || "").localeCompare(b.proyectoNombre || ""),
        ellipsis: true,
      },
      {
        title: "Cantidad Disponible",
        dataIndex: "disponible",
        key: "disponible",
        align: "right",
        sorter: (a, b) => (a.disponible || 0) - (b.disponible || 0),
      },
      {
        title: "Fecha De Caducidad",
        dataIndex: "caducidad",
        key: "caducidad",
        align: "center",
        sorter: (a, b) => (a.caducidad ? moment(a.caducidad).valueOf() : Infinity) - (b.caducidad ? moment(b.caducidad).valueOf() : Infinity),
        render: (value) => (value ? moment(value).format("DD/MM/YYYY") : "-"),
      },
      {
        title: "Días Para Vencer",
        dataIndex: "diasParaVencer",
        key: "diasParaVencer",
        align: "center",
        sorter: (a, b) => (a.diasParaVencer ?? Infinity) - (b.diasParaVencer ?? Infinity),
        render: (value, record) => {
          if (value === null || value === undefined) {
            return <Tag>Sin Caducidad</Tag>;
          }
          const bucket = buckets.find((b) => b.key === record.rango);
          return <Tag color={bucket?.color}>{value} {Math.abs(value) === 1 ? "día" : "días"}</Tag>;
        },
      },
    ];

    return (
      <>
        <Form
          ref={this.formRef}
          name="reporteLotes"
          onFinish={this.consultarReporteLotes.bind(this)}
          onFinishFailed={this.onFinishFailed}
        >
          <PageHeader
            onBack={this.atras.bind(this)}
            title="Reporte De Lotes"
            style={{
              border: "1px solid rgb(235, 237, 240)",
              backgroundColor: "white",
              marginBottom: 10,
            }}
            extra={
              <div style={{ display: "flex", justifyContent: "right" }}>
                <Space>
                  {this.state.dataReporte && (
                    <ButtonItem
                      blanco
                      onClick={() => this.descargarExcel()}
                      icon={<FileExcelOutlined />}
                      style={{ display: "block", margin: "0 0 0 auto" }}
                    >
                      Descargar Excel
                    </ButtonItem>
                  )}
                  <ButtonItem
                    type="primary"
                    htmlType="submit"
                    style={{ display: "block", margin: "0 0 0 auto" }}
                  >
                    Consultar
                  </ButtonItem>
                </Space>
              </div>
            }
          />
          <Spin spinning={this.state.loading}>
            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                paddingTop: 5,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
              }}
            >
              <Tabs
                style={{ margin: 0 }}
                tabBarExtraContent={{
                  left: <h1 style={{ padding: 0, margin: "0 10px 0 0" }}>Filtros:</h1>,
                }}
              >
                <TabPane tabKey="1" key="1" forceRender tab="Proyecto">
                  <FormItem name="proyectos" noStyle margin>
                    <SelectItem width="90%" mode="multiple" placeholder="Todos Los Proyectos">
                      {this.state.objetoProyectos}
                    </SelectItem>
                  </FormItem>
                </TabPane>
              </Tabs>
            </div>

            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                paddingBottom: 40,
                marginTop: 10,
                marginBottom: 10,
                height: 380,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
              }}
            >
              <h1 style={{ fontSize: 18, margin: 0 }}>Lotes Por Vencer</h1>
              <ResponsiveBar
                data={this.state.dataGrafico}
                keys={["Lotes"]}
                indexBy="rango"
                margin={{ top: 20, right: 20, bottom: 50, left: 60 }}
                padding={0.4}
                valueScale={{ type: "linear" }}
                indexScale={{ type: "band", round: true }}
                colors={(bar) => bar.data.color}
                borderRadius={3}
                borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: "Rango De Caducidad",
                  legendPosition: "middle",
                  legendOffset: 40,
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: "Cantidad De Lotes",
                  legendPosition: "middle",
                  legendOffset: -45,
                  format: (v) => (Number.isInteger(v) ? v : ""),
                }}
                enableLabel={true}
                tooltip={({ indexValue, value }) => (
                  <div
                    style={{
                      padding: "10px 14px",
                      background: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px",
                    }}
                  >
                    <strong>{indexValue}</strong>: {value} {value === 1 ? "lote" : "lotes"}
                  </div>
                )}
                role="application"
              />
            </div>

            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                paddingTop: 15,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
              }}
            >
              <Row gutter={[16, 16]} style={{ marginBottom: 15 }}>
                <Col span={8}>
                  <SelectItem
                    width="100%"
                    value={this.state.filtroRango}
                    onChange={(value) => this.setState({ filtroRango: value })}
                  >
                    <Option value="todos">Todos Los Rangos</Option>
                    {buckets.map((bucket) => (
                      <Option value={bucket.key} key={bucket.key}>
                        {bucket.label}
                      </Option>
                    ))}
                  </SelectItem>
                </Col>
                <Col span={10}>
                  <Input
                    placeholder="Buscar por lote, insumo o proyecto"
                    allowClear
                    value={this.state.busqueda}
                    onChange={(e) => this.setState({ busqueda: e.target.value })}
                  />
                </Col>
              </Row>
              <TableItem
                rowKey={(value) => value._id.toString()}
                columns={columns}
                dataSource={lotesFiltrados}
                size="small"
                pagination={{ pageSize: 50 }}
                onRow={(record) => {
                  if (
                    record.rango === BUCKET_SIN_CADUCIDAD.key ||
                    record.rango === BUCKET_FUERA_DE_RANGO.key ||
                    record.rango === "vigente"
                  ) {
                    return {};
                  }
                  const bucket = buckets.find((b) => b.key === record.rango);
                  const fondo = bucket ? suavizarColorCss(bucket.color) : undefined;
                  return fondo ? { style: { backgroundColor: fondo } } : {};
                }}
              />
            </div>
          </Spin>
        </Form>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
  };
};

export default connect(mapStateToProps)(ReporteLotes);
